
import { ToolRenderer } from '../../../components/tool-renderer';
import type { ToolCallData, ConnectorIcon } from '../../../components/tool-renderer';
import { useState, useEffect } from 'preact/hooks';
import { connectorRegistryService } from '../../../services/connectors/connector-registry.service';
import { useCurrentContext } from '../../../hooks/useCurrentContext';

// Cache connector metadata (icon + name) to avoid re-fetching per tool call
const connectorMetadataCache = new Map<string, { icon?: ConnectorIcon; name?: string }>();

interface ToolCallRendererProps {
  toolCall: ToolCallData;
  threadId?: string;
  messageId?: string;
}

export function ToolCallRenderer({ toolCall, threadId, messageId }: ToolCallRendererProps) {
  const context = useCurrentContext();

  // ToolCallRenderer always runs within a team context (chat threads are at /teams/:teamId/...)
  if (context.scope !== 'team') {
    console.error('ToolCallRenderer context:', context, 'URL:', window.location.pathname);
    throw new Error(`ToolCallRenderer requires team context. Current scope: ${context.scope}, URL: ${window.location.pathname}`);
  }

  const [enrichedToolCall, setEnrichedToolCall] = useState<ToolCallData>(toolCall);
  const [connectorIcon, setConnectorIcon] = useState<ConnectorIcon | undefined>(undefined);
  const [connectorName, setConnectorName] = useState<string | undefined>(undefined);

  useEffect(() => {
    const enhanceToolCall = async () => {
      // Backend always unwraps AI SDK's {type, value} format before storing
      const outputValue = toolCall.output;

      console.log('🎨 [ToolCallRenderer] useEffect running for:', toolCall.toolName);
      console.log('🎨 [ToolCallRenderer] Has componentUISpec?', !!outputValue?.componentUISpec);
      console.log('🎨 [ToolCallRenderer] Has permission?', !!outputValue?.permission);

      let enhancedToolCall: ToolCallData = { ...toolCall };

      // Step 1: Set appropriate description message based on approval state
      const isAwaitingApproval = outputValue?.requiresApproval === true;
      let computedMessage: string | undefined;

      if (isAwaitingApproval) {
        // Awaiting approval: prioritize input message (what's being requested)
        if (toolCall.args?.message && typeof toolCall.args.message === 'string') {
          computedMessage = toolCall.args.message;
        } else if (outputValue?.message) {
          computedMessage = outputValue.message;
        }
      } else {
        // Executed: prioritize output message (what happened)
        if (outputValue?.message) {
          computedMessage = outputValue.message;
        } else if (toolCall.args?.message && typeof toolCall.args.message === 'string') {
          computedMessage = toolCall.args.message;
        }
      }

      // Apply computed message to output (use unwrapped format)
      if (computedMessage) {
        enhancedToolCall = {
          ...enhancedToolCall,
          output: {
            ...outputValue,
            success: outputValue?.success ?? false,
            message: computedMessage
          }
        };
      }

      // Step 2: Fetch connector metadata (icon + name) for branding
      const parsedTool = connectorRegistryService.parseToolName(toolCall.toolName);
      if (parsedTool) {
        const cacheKey = `${context.teamId}:${parsedTool.connectorId}`;
        const cached = connectorMetadataCache.get(cacheKey);

        if (cached) {
          setConnectorIcon(cached.icon);
          setConnectorName(cached.name);
        } else {
          try {
            const connector = await connectorRegistryService.getConnector(context.teamId, parsedTool.connectorId);
            const metadata = { icon: connector.icon, name: connector.name };
            connectorMetadataCache.set(cacheKey, metadata);
            setConnectorIcon(metadata.icon);
            setConnectorName(metadata.name);
          } catch (error) {
            console.warn('🎨 [ToolCallRenderer] Failed to fetch connector metadata:', parsedTool.connectorId, error);
            // Cache the miss so we don't retry
            connectorMetadataCache.set(cacheKey, {});
          }
        }
      }

      // Step 3: Fetch and attach UI spec if needed
      if (outputValue?.permission || outputValue?.componentUISpec) {
        console.log('🎨 [ToolCallRenderer] Already has UI spec, skipping fetch');
        setEnrichedToolCall(enhancedToolCall);
        return;
      }

      try {
        console.log('🎨 [ToolCallRenderer] Parsed tool:', parsedTool);

        if (!parsedTool) {
          console.warn('🎨 [ToolCallRenderer] Failed to parse tool name:', toolCall.toolName);
          setEnrichedToolCall(enhancedToolCall);
          return;
        }

        // Fetch UI spec from connector service
        console.log('🎨 [ToolCallRenderer] Fetching UI spec for:', parsedTool.connectorId, parsedTool.toolName);
        const uiSpec = await connectorRegistryService.getUISpec(
          context.teamId,
          parsedTool.connectorId,
          parsedTool.toolName
        );

        console.log('🎨 [ToolCallRenderer] Fetched UI spec:', uiSpec);

        if (uiSpec && uiSpec.uiComponents && uiSpec.uiComponents.length > 0) {
          // Attach UI spec to tool call output
          const currentValue = enhancedToolCall.output;
          enhancedToolCall = {
            ...enhancedToolCall,
            output: {
              ...currentValue,
              success: currentValue?.success ?? false,
              permission: uiSpec.uiComponents[0],
              componentUISpec: uiSpec.uiComponents[0]
            }
          };
          console.log('🎨 [ToolCallRenderer] Setting enriched tool call with permission and componentUISpec');
        } else {
          console.warn('🎨 [ToolCallRenderer] UI spec is null/undefined');
        }
      } catch (error) {
        console.warn('🎨 [ToolCallRenderer] Failed to fetch UI spec for tool:', toolCall.toolName, error);
      }

      setEnrichedToolCall(enhancedToolCall);
    };

    enhanceToolCall();
  }, [toolCall]);

  // Auto-detect if buttons should be shown based on approval requirements
  const outputValue = enrichedToolCall.output;
  const showButtons = outputValue?.requiresApproval;

  const handleToolUpdate = (updatedToolCall: ToolCallData) => {
    console.log('Tool updated:', updatedToolCall);
    setEnrichedToolCall(updatedToolCall);
  };

  return <ToolRenderer toolCall={enrichedToolCall} showButtons={showButtons} onToolUpdate={handleToolUpdate} threadId={threadId} teamId={context.teamId} messageId={messageId} connectorIcon={connectorIcon} connectorName={connectorName} />;
}
